import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";

// ------------------------------------------------------
// ACCESS TOKEN MANAGEMENT
// ------------------------------------------------------
let accessToken = null;

// ------------------------------------------------------
// WORKSPACE MANAGEMENT
// ------------------------------------------------------
let currentWorkspaceId = null;

/**
 * Define o workspace atual.
 * Persiste no localStorage para recuperacao apos refresh da pagina.
 */
export const setCurrentWorkspace = (workspaceId) => {
  currentWorkspaceId = workspaceId;
  if (workspaceId) {
    localStorage.setItem('fastteam_workspace_id', String(workspaceId));
  } else {
    localStorage.removeItem('fastteam_workspace_id');
  }
};

/**
 * Retorna o workspace atual.
 * Verifica memoria primeiro, depois localStorage.
 */
export const getCurrentWorkspace = () => {
  if (currentWorkspaceId) return currentWorkspaceId;
  const saved = localStorage.getItem('fastteam_workspace_id');
  if (saved) {
    currentWorkspaceId = parseInt(saved, 10);
    return currentWorkspaceId;
  }
  return null;
};

/**
 * Limpa o workspace atual.
 */
export const clearCurrentWorkspace = () => {
  currentWorkspaceId = null;
  localStorage.removeItem('fastteam_workspace_id');
};

// ------------------------------------------------------
// RAW AXIOS INSTANCE (for refresh endpoint)
// ------------------------------------------------------
const raw = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

export const getAccessToken = async () => {
  if (accessToken) return accessToken;

  try {
    const response = await raw.post("/api/auth/refresh");
    const newToken = response.data.access_token;
    setAccessToken(newToken);
    return newToken;
  } catch {
    return null;
  }
};

export const setAccessToken = (token) => {
  accessToken = token;
};

export const clearAccessToken = () => {
  accessToken = null;
};

// ------------------------------------------------------
// MAIN AXIOS INSTANCE
// ------------------------------------------------------

export const api = axios.create({
  baseURL: API_URL,
  withCredentials: true, // Cookie HttpOnly
});

// ------------------------------------------------------
// REQUEST INTERCEPTOR
// Injects Authorization header and X-Workspace-ID header
// ------------------------------------------------------
api.interceptors.request.use(
  async (config) => {
    const token = await getAccessToken();

    config.headers = config.headers || {};

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Inject X-Workspace-ID header for workspace context
    const workspaceId = getCurrentWorkspace();
    if (workspaceId) {
      config.headers['X-Workspace-ID'] = String(workspaceId);
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ------------------------------------------------------
// RESPONSE INTERCEPTOR
// Handles 401 errors and automatic token refresh
// ------------------------------------------------------
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,

  async (error) => {
    const originalRequest = error.config;

    if (originalRequest?.url?.includes("/auth/refresh")) {
      return Promise.reject(error);
    }
    if (!error.response || error.response.status !== 401) {
      return Promise.reject(error);
    }

    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      // Use 'raw' instance (no interceptors) to avoid sending expired access token
      const res = await raw.post("/api/auth/refresh");
      const newToken = res.data.access_token;

      setAccessToken(newToken);
      processQueue(null, newToken);

      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return api(originalRequest);
    } catch (refreshErr) {
      processQueue(refreshErr, null);
      setAccessToken(null);
      return Promise.reject(refreshErr);
    } finally {
      isRefreshing = false;
    }
  }
);
